import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useLocation, useNavigate} from 'react-router-dom';
import appStore from '../../storage/AppStore';
import {post} from '../../common/requester';
import {getProduct} from '../../common/config';
import '../../common/club_search.css';
import ClubsMap from './ClubsMap';

const FILTER_KEYS = ['name', 'team_name', 'sport', 'sport_id', 'country', 'country_id', 'city', 'region_id', 'district_id', 'max_monthly_fee', 'birth_year'];
const PAGE_SIZE = 12;
const MAP_REQUEST_CONCURRENCY = 4;
const MAX_MAP_PAGES = 200;
const MAP_PAGE_SIZE = 200;
const FALLBACK_CENTER = [42.87676, 74.602019];
const userCenter = user => {
  if (user?.lat == null || (user?.lon == null && user?.lng == null)) return null;
  const lat = Number(user.lat), lng = Number(user.lon ?? user.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !(lat === 0 && lng === 0) ? [lat, lng] : null;
};
const readFilters = params => ({
  name: params.get('name') || '', team_name: params.get('team_name') || '',
  city: params.get('city') || '', country: params.get('country') || params.get('country_id') || '',
  sport: params.has('sport_id') ? `id:${params.get('sport_id')}` : params.has('sport') ? `text:${params.get('sport')}` : '',
  max_monthly_fee: params.get('max_monthly_fee') || '', birth_year: params.get('birth_year') || ''
});
const pageNumber = params => {const n = Number(params.get('page')); return Number.isSafeInteger(n) && n > 0 ? n : 1;};
const translate = key => appStore.tr?.[key] || key;

// The connection status comes from the API, never from whether a logo exists.
const isUnconnected = company => company?.is_connected === false || company?.status === 'pending';
const unconnectedLabel = () => {
  const product = getProduct() === 'course1' ? 'Course #1' : 'Club #1';
  const labels = {
    ru: 'Ещё не подключён к {product}',
    kg: '{product} системасына азырынча кошула элек',
    kz: '{product} жүйесіне әлі қосылмаған',
    uz: 'Hali {product} tizimiga ulanmagan',
    en: 'Not yet connected to {product}'
  };
  const template = appStore.tr?.club_search_not_connected || labels[appStore.user?.language] || labels.ru;
  return template.replace('{product}', product);
};
const unconnectedStyle = {display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '.77rem', lineHeight: 1.35, color: 'var(--brand-hint)', marginTop: '5px'};

const validateSearchResponse = data => {
  if (!data || (data.result != null && data.result !== 0 && data.result !== '0')) throw new Error(data?.message || data?.error || `api_result_${data?.result ?? 'empty'}`);
  if (data.error) throw new Error(data.error);
  if (!Array.isArray(data.companies)) throw new Error('invalid_response');
  return data;
};

// The map must see every matching club, not merely the 12 entries of one list page.
// Preserve backend filtering and page limits; fetch pages in small concurrent batches.
const loadMapResults = async request => {
  const first = validateSearchResponse(await post('clubs/search', {...request, page: 1}));
  const totalPages = Number(first.page_count) || 1;
  if (!Number.isSafeInteger(totalPages) || totalPages < 1 || totalPages > MAX_MAP_PAGES) throw new Error('too_many_map_results');
  const pages = [first];
  for (let start = 2; start <= totalPages; start += MAP_REQUEST_CONCURRENCY) {
    const numbers = Array.from({length: Math.min(MAP_REQUEST_CONCURRENCY, totalPages - start + 1)}, (_, i) => start + i);
    const batch = await Promise.all(numbers.map(page => post('clubs/search', {...request, page}).then(validateSearchResponse)));
    pages.push(...batch);
  }
  const unique = new Map();
  for (const page of pages) for (const company of page.companies) if (company?.id != null && !unique.has(company.id)) unique.set(company.id, company);
  return {...first, companies: [...unique.values()], page_count: 1};
};

const ClubLogo = ({club}) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [club.logo_url]);
  return <div className="csd-logo">
    {club.logo_url && !failed ? <img src={club.logo_url} alt="" loading="lazy" onError={() => setFailed(true)}/> : <i className="pi pi-shield" aria-hidden="true"/>}
  </div>;
};

const ClubCard = ({club, onOpen, language}) => {
  const place = [club.district, club.region, club.country].filter(Boolean).filter((item, index, items) => items.indexOf(item) === index).join(' · ');
  const teams = Array.isArray(club.teams) ? club.teams : [];
  const sports = Array.isArray(club.sports) ? club.sports : [];
  return <article className="csd-card">
    <div className="csd-card-top">
      <ClubLogo club={club}/>
      <div className="csd-card-main">
        <h2 className="csd-card-name">{club.name}</h2>
        {place && <p className="csd-card-place"><i className="pi pi-map-marker" aria-hidden="true"/>{place}</p>}
        {club.address && <p className="csd-card-address">{club.address}</p>}
        {sports.length > 0 && <div className="csd-sports">{sports.slice(0, 5).map(sport => <span className="csd-chip" key={sport.id}>{sport.name}</span>)}</div>}
        {isUnconnected(club) && <div className="csd-unconnected-note" style={unconnectedStyle}><i className="pi pi-info-circle" aria-hidden="true"/><span>{unconnectedLabel()}</span></div>}
      </div>
    </div>
    {teams.length > 0 && <div className="csd-teams">
      {teams.map(team => <div className="csd-team" key={team.id}>
        <div className="csd-team-content">
          <span className="csd-team-title">{team.name}</span>
          {team.sport && <span className="csd-team-sport">{team.sport}</span>}
          {team.schedule?.length > 0 && <span className="csd-schedule">{team.schedule.slice(0, 2).map(slot => `${appStore.tr?.[slot.day] || slot.day} ${slot.time_from}–${slot.time_to}`).join(' · ')}</span>}
        </div>
        {team.monthly_fee && <span className="csd-price">
          {Number(team.monthly_fee.amount).toLocaleString(language === 'kg' ? 'ru' : language === 'kz' ? 'ru' : language === 'uz' ? 'ru' : language === 'en' ? 'en' : 'ru')}
          {' '}{club.currency_sign || team.monthly_fee.currency || club.currency_id}
        </span>}
      </div>)}
    </div>}
    <div className="csd-card-footer">
      <button type="button" className="csd-more" onClick={() => onOpen(club.id)}>
        {translate('show_more')}<i className="pi pi-arrow-right" aria-hidden="true"/>
      </button>
    </div>
  </article>;
};

const ClubsSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCourse = getProduct() === 'course1';
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const filterKey = FILTER_KEYS.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params.get(key) || '')}`).join('&');
  const filters = useMemo(() => readFilters(new URLSearchParams(filterKey)), [filterKey]);
  const view = params.get('view') === 'map' ? 'map' : 'list';
  const page = view === 'map' ? 1 : pageNumber(params);
  const [draft, setDraft] = useState(filters);
  const [result, setResult] = useState(null);
  const mapViewportRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapStartCenter, setMapStartCenter] = useState(null);
  const positionRequestedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [dark, setDark] = useState(() => {
    const scheme = window.Telegram?.WebApp?.colorScheme;
    return scheme ? scheme === 'dark' : window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
  });
  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const updateDark = () => setDark(webApp?.colorScheme ? webApp.colorScheme === 'dark' : Boolean(mq?.matches));
    webApp?.onEvent?.('themeChanged', updateDark);
    mq?.addEventListener?.('change', updateDark);
    updateDark();
    return () => {webApp?.offEvent?.('themeChanged', updateDark); mq?.removeEventListener?.('change', updateDark);};
  }, []);
  const language = appStore.user?.language || 'ru';
  const sports = (Array.isArray(appStore.sports) ? appStore.sports : []).filter(item => item && item.id != null && item.name);

  useEffect(() => setDraft(filters), [filters]);

  const request = {};
  FILTER_KEYS.forEach(key => {const value = params.get(key); if (value !== null && value !== '') request[key] = value;});
  request.page = page;
  request.page_size = PAGE_SIZE;
  request.language = language;
  const requestKey = JSON.stringify(request);

  // /api/init initializes anonymous user.lat/lon using IP geolocation.
  // A signed-in user can have no saved coordinates: resolve IP once using /api/location.
  useEffect(() => {
    if (view !== 'map' || mapStartCenter || !appStore.user?.initialized || positionRequestedRef.current) return;
    const center = userCenter(appStore.user);
    if (center) {setMapStartCenter(center); return;}
    positionRequestedRef.current = true;
    appStore.getLocation().then(data => setMapStartCenter(userCenter(data) || FALLBACK_CENTER))
      .catch(() => setMapStartCenter(FALLBACK_CENTER));
  }, [view, mapStartCenter, appStore.user?.initialized, appStore.user?.lat, appStore.user?.lon, appStore.user?.lng]);

  // Leaflet events update a ref only. They MUST NOT trigger data loading.
  const onMapBoundsChange = useCallback(viewport => {
    mapViewportRef.current = viewport;
    setMapReady(ready => ready || true); // initial map readiness only
  }, []);
  const searchCurrentArea = useCallback(() => setRefresh(n => n + 1), []);

  useEffect(() => {if (view !== 'map') setMapReady(false);}, [view]);

  useEffect(() => {
    if (view !== 'list') return;
    let active = true;
    setLoading(true);
    setResult(null);
    setError('');
    post('clubs/search', JSON.parse(requestKey)).then(validateSearchResponse).then(data => {
      if (active) setResult(data);
    }).catch(exc => {
      if (!active) return;
      console.error('Club list API error:', exc);
      setError(exc?.message || 'request_failed');
    }).finally(() => {if (active) setLoading(false);});
    return () => {active = false;};
  }, [requestKey, refresh, view]);

  useEffect(() => {
    if (view !== 'map' || !mapReady || !mapViewportRef.current?.bounds) return;
    let active = true;
    setLoading(true);
    setResult(null);
    setError('');
    const request = {...JSON.parse(requestKey), view: 'map', bounds: mapViewportRef.current.bounds, page: 1, page_size: MAP_PAGE_SIZE};
    loadMapResults(request).then(data => {if (active) setResult(data);}).catch(exc => {
      if (!active) return;
      console.error('Club map API error:', exc);
      setError(exc?.message || 'request_failed');
    }).finally(() => {if (active) setLoading(false);});
    return () => {active = false;};
  }, [requestKey, refresh, view, mapReady]);

  const change = (key, value) => setDraft(current => ({...current, [key]: value}));
  const submit = e => {
    e?.preventDefault();
    const next = new URLSearchParams(location.search); // Preserve Telegram jwt, remove only controlled search filters.
    FILTER_KEYS.forEach(key => next.delete(key));
    for (const key of ['name', 'team_name', 'country', 'city', 'max_monthly_fee', 'birth_year']) {
      if (draft[key]?.trim()) next.set(key, draft[key].trim());
    }
    if (draft.sport.startsWith('id:')) next.set('sport_id', draft.sport.slice(3));
    else if (draft.sport.startsWith('text:')) next.set('sport', draft.sport.slice(5));
    next.delete('page');
    const target = `/clubs/search?${next.toString()}`;
    if (target === `${location.pathname}${location.search}`) setRefresh(n => n + 1);
    else navigate(target);
  };
  const clear = () => {
    const next = new URLSearchParams(location.search);
    FILTER_KEYS.forEach(key => next.delete(key));
    next.delete('page');
    setDraft(readFilters(next));
    navigate(`/clubs/search${next.toString() ? `?${next.toString()}` : ''}`);
  };
  const setView = nextView => {
    if (nextView === view) return;
    const next = new URLSearchParams(location.search);
    if (nextView === 'map') {next.set('view', 'map'); next.delete('page'); setMapReady(false);}
    else {next.delete('view'); next.delete('page');}
    navigate(`/clubs/search${next.toString() ? `?${next.toString()}` : ''}`);
  };
  const goPage = nextPage => {
    if (nextPage < 1 || (result?.page_count && nextPage > result.page_count)) return;
    const next = new URLSearchParams(location.search);
    next.set('page', String(nextPage));
    navigate(`/clubs/search?${next.toString()}`);
  };
  const count = Number(result?.count || 0);
  const countLabel = translate('club_search_results_count').replace('{count}', count.toLocaleString());
  const translatedError = error === 'country_required_for_fee' ? translate('club_search_fee_country_required') : translate('club_search_error');

  return <main className="club-search-directory" data-theme={dark ? 'dark' : 'light'}>
    <header className="csd-header">
      <button type="button" className="csd-back" onClick={() => navigate('/')} aria-label={translate('back')}><i className="pi pi-arrow-left"/></button>
      <div>
        <h1 className="csd-title">{translate('club_search_directory_title')}</h1>
        <p className="csd-subtitle">{isCourse ? appStore.tr.course_search_subtitle : appStore.tr.club_search_subtitle}</p>
      </div>
    </header>

    <form className="csd-filters" onSubmit={submit}>
      <div className="csd-filter-heading"><i className="pi pi-sliders-h" aria-hidden="true"/>{translate('club_search_filters')}</div>
      <div className="csd-filter-grid">
        <label className="csd-field"><span>{appStore.tr.club_search_name}</span><input value={draft.name} onChange={e => change('name', e.target.value)} maxLength={100} placeholder={appStore.tr.club_search_name_hint}/></label>
        <label className="csd-field"><span>{translate('club_search_team_name')}</span><input value={draft.team_name} onChange={e => change('team_name', e.target.value)} maxLength={100} placeholder={translate('club_search_team_hint')}/></label>
        <label className="csd-field"><span>{isCourse ? appStore.tr.course_search_subject : appStore.tr.club_search_sport}</span><select value={draft.sport} onChange={e => change('sport', e.target.value)}>
          <option value="">{isCourse ? appStore.tr.course_search_all_subjects : appStore.tr.club_search_all_sports}</option>
          {draft.sport.startsWith('text:') && <option value={draft.sport}>{draft.sport.slice(5)}</option>}
          {sports.map(item => <option value={`id:${item.id}`} key={item.id}>{item.icon ? `${item.icon} ` : ''}{item.name}</option>)}
        </select></label>
        <label className="csd-field"><span>{translate('club_search_country')}</span><input value={draft.country} onChange={e => change('country', e.target.value)} maxLength={100} placeholder={translate('club_search_country_hint')}/></label>
        <label className="csd-field"><span>{appStore.tr.club_search_city}</span><input value={draft.city} onChange={e => change('city', e.target.value)} maxLength={100} placeholder={appStore.tr.club_search_city_hint}/></label>
        <label className="csd-field"><span>{translate('club_search_fee_max')}</span><input type="number" min="0" max="1000000000" value={draft.max_monthly_fee} onChange={e => change('max_monthly_fee', e.target.value)} placeholder={translate('club_search_fee_hint')}/></label>
        <label className="csd-field"><span>{translate('club_search_birth_year')}</span><input type="number" min="1900" max="2100" value={draft.birth_year} onChange={e => change('birth_year', e.target.value)} placeholder="2013"/></label>
      </div>
      <div className="csd-filter-actions">
        <button type="button" className="csd-clear" onClick={clear}><i className="pi pi-filter-slash" aria-hidden="true"/>{translate('club_search_reset')}</button>
        <button type="submit" className="csd-submit"><i className="pi pi-search" aria-hidden="true"/>{isCourse ? appStore.tr.course_search_button : appStore.tr.club_search_button}</button>
      </div>
    </form>

    <section className="csd-results" aria-live="polite">
      <div className="csd-results-header">
        <h2>{countLabel}</h2>
        <div className="csd-results-controls">
          {loading && <span className="csd-loading"><i className="pi pi-spin pi-spinner" aria-hidden="true"/>{translate('loading')}</span>}
          <div className="csd-view-switch" role="group" aria-label={translate('club_search_view_map')}>
            <button type="button" aria-pressed={view === 'list'} className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><i className="pi pi-list" aria-hidden="true"/>{translate('club_search_view_list')}</button>
            <button type="button" aria-pressed={view === 'map'} className={view === 'map' ? 'active' : ''} onClick={() => setView('map')}><i className="pi pi-map" aria-hidden="true"/>{translate('club_search_view_map')}</button>
          </div>
        </div>
      </div>
      {error && <div className="csd-notice csd-notice-error" role="alert"><span>{translatedError}</span><button type="button" onClick={() => setRefresh(n => n + 1)}>{translate('club_search_retry')}</button></div>}
      {view === 'map' ? <>
        {result?.age_eligibility_verified === false && result.birth_year_requested && <p className="csd-info">{translate('club_search_age_unverified')}</p>}
        {mapStartCenter ? <ClubsMap companies={result?.companies || []} loading={loading}
                  onBoundsChange={onMapBoundsChange} onSearch={searchCurrentArea}
                  initialCenter={mapViewportRef.current?.center || mapStartCenter}
                  initialZoom={mapViewportRef.current?.zoom || 12}
                  selectedSportId={params.get('sport_id') || ''} selectedSport={params.get('sport') || ''}/> :
          <div className="csd-loading" role="status"><i className="pi pi-spin pi-spinner" aria-hidden="true"/>{translate('loading')}</div>}
      </> : !result && loading ? <div className="csd-grid" aria-hidden="true">{[1, 2, 3, 4].map(id => <div className="csd-skeleton" key={id}/>)}</div> :
        result && <>
          {result.age_eligibility_verified === false && result.birth_year_requested && <p className="csd-info">{translate('club_search_age_unverified')}</p>}
          {result.companies.length ? <div className={`csd-grid${loading ? ' csd-grid-pending' : ''}`}>
            {result.companies.map(club => <ClubCard key={club.id} club={club} language={language} onOpen={id => navigate(`/club/${id}`)}/>)}
          </div> : !loading && !error && <div className="csd-empty"><i className="pi pi-search" aria-hidden="true"/><h3>{translate('club_search_no_results')}</h3><p>{translate('club_search_no_results_hint')}</p></div>}
          {result.page_count > 1 && <nav className="csd-pagination" aria-label={translate('club_search_pagination')}>
            <button type="button" disabled={loading || page <= 1} onClick={() => goPage(page - 1)}><i className="pi pi-chevron-left" aria-hidden="true"/>{translate('club_search_previous')}</button>
            <span>{translate('club_search_page_of').replace('{page}', page).replace('{total}', result.page_count)}</span>
            <button type="button" disabled={loading || page >= result.page_count} onClick={() => goPage(page + 1)}>{translate('club_search_next')}<i className="pi pi-chevron-right" aria-hidden="true"/></button>
          </nav>}
        </>}
    </section>
  </main>;
};

export default observer(ClubsSearch);
